import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import usersGroupDarkIcon from '@/assets/images/users-group-dark.svg';
import bookmarkWhiteSmallIcon from '@/assets/images/bookmark-white-small.svg';
import lockAccentIcon from '@/assets/images/lock-accent.svg';
import { Tag } from '@/components/ui/tag';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

export type SaveTemplateVisibility = 'private' | 'shared_with_practice';

export type SaveAsTemplatePayload = {
  templateName: string;
  description: string;
  suitableFor: string;
  clinicalTags: string[];
  visibility: SaveTemplateVisibility;
};

type SaveAsTemplateModalProps = {
  open: boolean;
  onClose: () => void;
  dayLabel: string;
  mealCount: number;
  totalKcal: number;
  templateKind?: 'meal_plan' | 'meal_slot';
  subtitle?: string;
  summaryDetail?: string;
  onSave?: (payload: SaveAsTemplatePayload) => void | Promise<void>;
};

const clinicalTagOptions = [
  'Type 1 Diabetes',
  'Type 2 Diabetes',
  'LADA (Autoimmune Type 2)',
  'Gestational Diabetes',
  'Insulin Resistance',
  'PCOS (Polycystic Ovary Syndrome)',
  "Hashimoto's Thyroiditis",
  'Hyperthyroidism',
  'Hypothyroidism',
  'Diabetic Friendly',
  'Thyroid Support',
  'PCOS Protocol',
  'Low-GI',
  'Anti-inflammatory',
  'High Protein',
  'Low Carb',
  'Mediterranean',
  'Vegetarian',
  'Vegan',
  'Gluten-Free',
  'Dairy-Free',
  'Low Sodium',
  'High Fibre',
  'Calorie Deficit',
  'Maintenance',
  'Muscle Gain',
];

const defaultSelectedTags: string[] = [];

export const SaveAsTemplateModal: React.FC<SaveAsTemplateModalProps> = ({
  open,
  onClose,
  dayLabel,
  mealCount,
  totalKcal,
  templateKind = 'meal_slot',
  subtitle,
  summaryDetail,
  onSave,
}) => {
  const [templateName, setTemplateName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [suitableFor, setSuitableFor] = React.useState('');
  const [selectedTags, setSelectedTags] = React.useState<string[]>(defaultSelectedTags);
  const [visibility, setVisibility] = React.useState<SaveTemplateVisibility>('private');
  const [templateNameError, setTemplateNameError] = React.useState('');
  const [clinicalTagsError, setClinicalTagsError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMealPlanTemplate = templateKind === 'meal_plan';

  React.useEffect(() => {
    if (!open) return;
    setTemplateName('');
    setDescription('');
    setSuitableFor('');
    setSelectedTags(defaultSelectedTags);
    setVisibility('private');
    setTemplateNameError('');
    setClinicalTagsError('');
    setIsSubmitting(false);
  }, [open, dayLabel]);

  const toggleTag = React.useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag],
    );
    if (clinicalTagsError) setClinicalTagsError('');
  }, [clinicalTagsError]);

  const handleSave = React.useCallback(async () => {
    const trimmedTemplateName = templateName.trim();
    if (!trimmedTemplateName) {
      setTemplateNameError('Template name is required.');
      return;
    }
    if (!selectedTags.length) {
      setClinicalTagsError('Please select at least one clinical tag.');
      return;
    }

    setTemplateNameError('');
    setClinicalTagsError('');
    setIsSubmitting(true);
    try {
      await onSave?.({
        templateName: trimmedTemplateName,
        description: description.trim(),
        suitableFor: suitableFor.trim(),
        clinicalTags: selectedTags,
        visibility,
      });
      onClose();
    } catch {
      // Parent handler shows the backend error toast and keeps the dialog open.
    } finally {
      setIsSubmitting(false);
    }
  }, [
    onSave,
    templateName,
    description,
    suitableFor,
    selectedTags,
    visibility,
    onClose,
  ]);

  const resolvedSubtitle =
    subtitle ??
    (isMealPlanTemplate
      ? 'Save this meal plan for reuse across clients'
      : 'Save this meal slot for reuse across meal plans');
  const resolvedSummaryDetail =
    summaryDetail ??
    `${mealCount} meals · ${Intl.NumberFormat('en-US').format(totalKcal)} kcal total`;
  const tagLabel = isMealPlanTemplate ? 'Tags' : 'Clinical Tags';

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 1rem)', sm: '490px' },
          maxHeight: 'calc(100vh - 4rem)',
          borderRadius: '14px',
          bgcolor: '#FFFFFF',
        },
      }}
    >
      <DialogContent sx={{ p: 0, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#FFFFFF' }}>
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.25,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '14px',
                bgcolor: '#EAF9FF',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <BookmarkRoundedIcon sx={{ fontSize: 18, color: '#20AEDC' }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '20px',
                  lineHeight: '25px',
                  fontWeight: 700,
                  letterSpacing: '-0.45px',
                  color: '#1E2939',
                }}
              >
                Save as Template
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  lineHeight: '19.5px',
                  letterSpacing: '-0.08px',
                  color: '#9CA3AF',
                }}
              >
                {resolvedSubtitle}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              width: 32,
              height: 32,
              bgcolor: '#20AEDC',
              border: '1px solid #FFFFFF',
              color: '#fff',
              '&:hover': { bgcolor: '#1B9FC8' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pb: 0, overflowY: 'auto' }}>
          <Box
            sx={{
              p: '16px',
              mb: '20px',
              borderRadius: '14px',
              bgcolor: '#F8FAFC',
              border: '1px solid #F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 62,
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '15px',
                  lineHeight: '22.5px',
                  letterSpacing: '-0.23px',
                  fontWeight: 700,
                  color: '#1E2939',
                }}
              >
                {dayLabel}
              </Typography>
              <Typography
                sx={{
                  mt: '1px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#0A0A0A',
                }}
              >
                {resolvedSummaryDetail}
              </Typography>
            </Box>
         
      
          </Box>

          <Typography
            sx={{
              mb: 1,
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              lineHeight: '19.5px',
              letterSpacing: '-0.08px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Template Name <Box component="span" sx={{ color: '#F87171' }}>*</Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={templateName}
            placeholder="Template Name"
            onChange={(event) => {
              setTemplateName(event.target.value);
              if (templateNameError) setTemplateNameError('');
            }}
            error={Boolean(templateNameError)}
            helperText={templateNameError}
            sx={{
              mb: '20px',
              '& .MuiOutlinedInput-root': {
                minHeight: '45px',
                borderRadius: '14px',
                fontSize: '14px',
                letterSpacing: '-0.15px',
                color: '#0A0A0A',
                bgcolor: '#FFFFFF',
                '& fieldset': {
                  borderColor: '#20AEDC',
                  borderWidth: '1.5px',
                },
                '&:hover fieldset': {
                  borderColor: '#20AEDC',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#20AEDC',
                  borderWidth: '1.5px',
                },
              },
              '& .MuiOutlinedInput-input': {
                px: '16px',
                py: '10px',
                fontSize: '13px',
                color: '#0A0A0A',
                '&::placeholder': {
                  color: 'rgba(30, 41, 57, 0.50)',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  opacity: 1,
                },
              },
            }}
          />

          <Typography
            sx={{
              mb: 1,
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              lineHeight: '19.5px',
              letterSpacing: '-0.08px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Description <Box component="span" sx={{ color: '#98A2B3', fontWeight: 500 }}>(optional)</Box>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Brief notes about this template's purpose, conditions it suits, or meal highlights."
            sx={{
              mb: '20px',
              '& .MuiOutlinedInput-root': {
                minHeight: '84.5px',
                borderRadius: '14px',
                fontSize: '13px',
                lineHeight: '19.5px',
                letterSpacing: '-0.08px',
                bgcolor: '#fff',
                '& fieldset': { borderColor: '#E5E7EB' },
              },
              '& .MuiOutlinedInput-input': {
                px: '16px',
                py: '12px',
                color: '#0A0A0A',
                '&::placeholder': {
                  color: 'rgba(30, 41, 57, 0.50)',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  opacity: 1,
                },
              },
            }}
          />

          <Typography
            sx={{
              mb: 1,
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              lineHeight: '19.5px',
              letterSpacing: '-0.08px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Suitable For <Box component="span" sx={{ color: '#98A2B3', fontWeight: 500 }}>(Condition)</Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={suitableFor}
            onChange={(event) => setSuitableFor(event.target.value)}
            placeholder="Example - PCOS + Diabetes"
            sx={{
              mb: '20px',
              '& .MuiOutlinedInput-root': {
                minHeight: '43px',
                borderRadius: '14px',
                fontSize: '14px',
                letterSpacing: '-0.15px',
                color: '#0A0A0A',
                bgcolor: '#fff',
                '& fieldset': { borderColor: '#E5E7EB' },
              },
              '& .MuiOutlinedInput-input': { px: '16px', py: '10px', color: '#0A0A0A' },
              '& .MuiOutlinedInput-input::placeholder': {
                color: 'rgba(30, 41, 57, 0.50)',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                opacity: 1,
              },
            }}
          />

          <Typography
            sx={{
              mb: 1.5,
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              lineHeight: '19.5px',
              letterSpacing: '-0.08px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            {tagLabel}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '8px', mb: '20px' }}>
            {clinicalTagOptions.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return (
                <Tag
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  selected={isSelected}
                  variant="outlined"
                  size="md"
                  startIcon={isSelected ? <CheckRoundedIcon sx={{ fontSize: 11 }} /> : undefined}
                  className={`h-8 bg-white font-[Inter,sans-serif] text-xs ${
                    isSelected
                      ? 'border-[#20AEDC] font-semibold text-[#20AEDC]'
                      : 'border-[#D1D5DC] font-normal text-[#6B7280]'
                  }`}
                >
                  {formatUnderscoreDisplayLabel(tag)}
                </Tag>
              );
            })}
          </Stack>
          {clinicalTagsError ? (
            <Typography
              sx={{
                mt: '-12px',
                mb: '20px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                lineHeight: '18px',
                color: '#d32f2f',
              }}
            >
              {clinicalTagsError}
            </Typography>
          ) : null}

          <Typography
            sx={{
              mb: 1.5,
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              lineHeight: '19.5px',
              letterSpacing: '-0.08px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Visibility
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '12px',
              mb: '20px',
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setVisibility('private')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setVisibility('private');
                }
              }}
              sx={{
                borderRadius: '14px',
                border: visibility === 'private' ? '2px solid #20AEDC' : '2px solid #E5E7EB',
                bgcolor: visibility === 'private' ? '#F0FBFF' : '#fff',
                p: '18px',
                minHeight: 95,
                cursor: 'pointer',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box component="img" src={lockAccentIcon} alt="Private" sx={{ width: 24, height: 24 }} />
                <Box sx={{ width: '151px' }}>
                  <Box sx={{ height: '21px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '21px', letterSpacing: '-0.15px', fontWeight: 700, color: visibility === 'private' ? '#20AEDC' : '#374151' }}>
                      Private
                    </Typography>
                    {visibility === 'private' ? <CheckRoundedIcon sx={{ fontSize: 14, color: '#20AEDC' }} /> : <Box sx={{ width: 14, height: 14 }} />}
                  </Box>
                  <Typography sx={{ mt: '2px', fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '18px', fontWeight: 500, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                    Only visible to you
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
              role="button"
              tabIndex={0}
              onClick={() => setVisibility('shared_with_practice')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setVisibility('shared_with_practice');
                }
              }}
              sx={{
                borderRadius: '14px',
                border: visibility === 'shared_with_practice' ? '2px solid #20AEDC' : '2px solid #E5E7EB',
                bgcolor: visibility === 'shared_with_practice' ? '#F0FBFF' : '#fff',
                p: '18px',
                minHeight: 95,
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box component="img" src={usersGroupDarkIcon} alt="Public" sx={{ width: 24, height: 24 }} />
                <Box sx={{ width: '151px' }}>
                  <Box sx={{ height: '21px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: visibility === 'shared_with_practice' ? 0 : '7.883px' }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '21px', letterSpacing: '-0.1504px', fontWeight: 700, color: visibility === 'shared_with_practice' ? '#20AEDC' : '#374151', whiteSpace: 'nowrap' }}>
                      Public
                    </Typography>
                    {visibility === 'shared_with_practice' ? <CheckRoundedIcon sx={{ fontSize: 14, color: '#20AEDC' }} /> : null}
                  </Box>
                  <Typography sx={{ mt: '2px', fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '18px', fontWeight: 500, color: '#9CA3AF', width: '134px' }}>
                    Other practitioners can use this
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

        </Box>

        <Box
          sx={{
            px: 3,
            pt: '1px',
            pb: 0,
            minHeight: '80px',
            borderTop: '1px solid #F3F4F6',
            bgcolor: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              height: 47,
              borderRadius: '14px',
              textTransform: 'none',
              borderColor: '#E5E7EB',
              color: '#374151',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.15px',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={isSubmitting}
            startIcon={<Box component="img" src={bookmarkWhiteSmallIcon} alt="Save template" sx={{ width: 15, height: 15 }} />}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              height: 45,
              borderRadius: '14px',
              textTransform: 'none',
              bgcolor: '#20AEDC',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '-0.15px',
              '&:hover': { bgcolor: '#1498c2' },
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save Template'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
